import { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// Confidential crypto primitives (provided by the @fairblock/stabletrust SDK)
import init from './confidentialCrypto.js';
// SDK-backed ElGamal key derivation (identical EIP-712 domain + HKDF context,
// so derived keys match the app's historical derivation and existing accounts
// stay valid).
import { deriveElGamalKeypair } from '../services/stabletrustService.js';

// Import secure key cache
import { secureKeyCache } from '../services/secureKeyCache.js';
import { getCleanErrorMessage, isUserRejectedError } from '../utils/errorParser.js';

interface KeypairResult {
  public_key: string;
  private_key: string;
}

interface UseDeterministicElGamalKeypairResult {
  keypair: KeypairResult | null;
  loading: boolean;
  error: string | null;
  needsConsent: boolean;
}

export function useDeterministicElGamalKeypair(
  provider: ethers.providers.Web3Provider | null,
  account: string | null,
  chainId: number | null,
  verifyingContract: string | null,
  consentGiven: boolean = false
): UseDeterministicElGamalKeypairResult {
  const [keypair, setKeypair] = useState<KeypairResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [needsConsent, setNeedsConsent] = useState<boolean>(false);

  useEffect(() => {
    if (!provider || !account || !chainId || !verifyingContract) {
      setKeypair(null);
      setError(null);
      setNeedsConsent(false);
      setLoading(false);
      return;
    }

    // Drop the previous chain's keypair immediately so callers never keep
    // using Sepolia keys after switching to Arbitrum One (or vice versa)
    // while the async cache lookup is in flight.
    setKeypair(null);
    setError(null);
    setNeedsConsent(false);

    // Normalize address to lowercase for consistent key derivation and cache lookups
    const accountLower = account.toLowerCase();
    let cancelled = false;

    const checkAndDeriveKeypair = async () => {
      try {
        // Initialize secure key cache
        await secureKeyCache.initialize();
        if (cancelled) return;

        // Try to retrieve from secure cache first
        const cachedKeypair = await secureKeyCache.retrieveKeypair(accountLower, chainId, verifyingContract);
        if (cancelled) return;
        if (cachedKeypair) {
          // Mark as connected since cached keys exist (means they've connected before)
          secureKeyCache.markAsConnected(accountLower, chainId, verifyingContract);
          setKeypair(cachedKeypair);
          setNeedsConsent(false);
          return;
        }

        // No cached keypair - check if consent has been given
        if (!consentGiven) {
          setNeedsConsent(true);
          setKeypair(null);
          return;
        }

        // Consent given, proceed with derivation
        setNeedsConsent(false);
        await deriveKeypair();
      } catch (err) {
        if (cancelled) return;
        console.error('Error checking cache:', err);
        setError(getCleanErrorMessage(err));
      }
    };

    const deriveKeypair = async (retryCount = 0) => {
      const MAX_RETRIES = 2; // Will try up to 3 times total (initial + 2 retries)
      
      setLoading(true);
      setError(null);

      try {

        // Initialize WASM module if not already done
        await init();

        // Use the provider passed in (which should be the active wallet provider)
        // Get the underlying EIP-1193 provider from the ethers provider
        let ethereum = null;
        if (provider) {
          // Extract the underlying EIP-1193 provider from the ethers.Web3Provider
          ethereum = (provider as any).provider || provider;
        }
        
        // No window.ethereum fallback on purpose: the connected wallet is
        // always passed in, and reaching for whatever extension happens to be
        // injected would derive the keypair from the wrong wallet's signature.
        // Without a provider this throws below, which is the right outcome.

        // On mobile, wait longer for provider to be ready
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                        (typeof window !== 'undefined' && window.innerWidth <= 768);
        
        // Wait for provider to be ready using readiness probe (don't rely on private _state)
        // On mobile, this may take longer
        const maxWaitTime = isMobile ? 2000 : 500;
        let waited = 0;
        let isReady = false;
        
        if (ethereum) {
          while (!isReady && waited < maxWaitTime) {
            try {
              // Cheap readiness probe - try to get chainId
              await ethereum.request({ method: 'eth_chainId' });
              isReady = true;
            } catch (e) {
              // Not ready yet, wait and retry
              await new Promise(resolve => setTimeout(resolve, 100));
              waited += 100;
            }
          }
          
          if (!isReady) {
            console.warn('Provider readiness check timed out for key derivation, continuing anyway');
          }
        }

        // Ensure wallet is unlocked before attempting to sign
        // This forces the unlock popup if wallet is locked
        try {
          if (ethereum) {
            // Check if wallet is locked by trying a lightweight operation
            await ethereum.request({
              method: 'eth_getBalance',
              params: [accountLower, 'latest']
            });
          }
        } catch (lockCheckError) {
          // Wallet is locked - trigger unlock popup
          if (ethereum) {
            try {
              // Try wallet_requestPermissions first (if supported)
              try {
                await ethereum.request({
                  method: 'wallet_requestPermissions',
                  params: [{ eth_accounts: {} }]
                });
              } catch (permError) {
                // If wallet_requestPermissions is not supported (code -32004 or -32601),
                // fallback to eth_requestAccounts
                if (permError.code === -32004 || permError.code === -32601 || 
                    (permError.message && permError.message.includes('not supported'))) {
                  await ethereum.request({ method: 'eth_requestAccounts' });
                } else {
                  // Re-throw other errors (user rejection, etc.)
                  throw permError;
                }
              }
              
              // Verify unlock was successful
              await ethereum.request({
                method: 'eth_getBalance',
                params: [accountLower, 'latest']
              });
            } catch (unlockError) {
              // If unlock fails, log but don't throw - we'll try signing anyway
              // The signing request itself will trigger the unlock popup if needed
              console.warn('Could not unlock wallet, will attempt signing anyway:', unlockError);
            }
          }
        }

        // Resolve a raw EIP-1193 provider for the SDK (which builds an ethers v6
        // signer internally). Prefer the already-extracted `ethereum`, falling
        // back to the underlying provider of the passed-in ethers v5 provider.
        const eip1193 =
          ethereum && typeof ethereum.request === 'function'
            ? ethereum
            : (provider as any)?.provider && typeof (provider as any).provider.request === 'function'
              ? (provider as any).provider
              : null;
        if (!eip1193) {
          throw new Error('No EIP-1193 provider available for key derivation');
        }

        // Derive the deterministic ElGamal keypair via the StableTrust SDK. The
        // SDK signs the same EIP-712 message (ConfidentialTokens / DeriveElGamalKey)
        // and uses the same HKDF domain context as before, so the resulting keys
        // are identical to the app's historical derivation.
        const result = await deriveElGamalKeypair(
          eip1193,
          { chainId, contractAddress: verifyingContract },
          accountLower
        );

        if (!result || !result.private_key || !result.public_key) {
          throw new Error('Keypair derivation failed - invalid result');
        }

        // Store in secure cache
        await secureKeyCache.storeKeypair(accountLower, chainId, verifyingContract, result);
        // Mark as connected when keys are successfully derived and stored
        secureKeyCache.markAsConnected(accountLower, chainId, verifyingContract);


        setKeypair(result);
      } catch (err) {
        // Retry logic - silently retry without logging to user. Never after the
        // user rejected the signature: that would re-open the wallet prompt
        // they just closed.
        if (retryCount < MAX_RETRIES && !isUserRejectedError(err)) {
          // Wait a bit before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
          return deriveKeypair(retryCount + 1);
        }
        
        // Only log error if all retries failed
        console.error('Error deriving keypair after retries:', err);
        setError(getCleanErrorMessage(err));
        setKeypair(null);
      } finally {
        setLoading(false);
      }
    };

    checkAndDeriveKeypair();

    return () => {
      cancelled = true;
    };
  }, [provider, account, chainId, verifyingContract, consentGiven]);

  return { keypair, loading, error, needsConsent };
}
